import { useInView } from '@/hooks/useInView';

const team = [
  {
    initials: 'I',
    role: 'Founder & Principal',
    image: 'https://public.readdy.ai/ai/img_res/c71723db3063e99200779a2302cdf0ad.jpg',
    tagline: 'The Architect',
    hint: 'Two decades. Four continents. One standard.',
    traits: ['Strategic Vision', 'Global Portfolio', 'Founding Principal'],
    detail: 'The founding intelligence behind every property relationship. Operates across markets with a precision that leaves nothing to interpretation.',
  },
  {
    initials: 'II',
    role: 'Director of Client Relations',
    image: 'https://readdy.ai/api/search-image?query=mysterious%20elegant%20silhouette%20of%20a%20person%20standing%20near%20floor%20to%20ceiling%20window%20overlooking%20city%20at%20dusk%2C%20dramatic%20backlighting%2C%20dark%20sophisticated%20atmosphere%2C%20luxury%20penthouse%20setting%2C%20editorial%20fashion%20photography%20style%2C%20deep%20shadows%2C%20warm%20golden%20rim%20light%2C%20identity%20obscured%2C%20cinematic%20mood%2C%20high%20contrast&width=480&height=600&seq=team-mystery-01&orientation=portrait',
    tagline: 'The Liaison',
    hint: 'Every client relationship begins and ends here.',
    traits: ['Client Discretion', 'Relationship Architecture', 'By Referral Only'],
    detail: 'The singular point of contact for every client. Trust is not assumed — it is earned, then protected with absolute care.',
  },
  {
    initials: 'III',
    role: 'Head of Asset Operations',
    image: 'https://readdy.ai/api/search-image?query=mysterious%20silhouette%20of%20a%20commanding%20figure%20seated%20at%20a%20large%20dark%20executive%20desk%20surrounded%20by%20architectural%20blueprints%20and%20documents%2C%20low%20dramatic%20lighting%20from%20a%20single%20overhead%20lamp%2C%20deep%20shadows%20filling%20the%20room%2C%20luxury%20private%20office%20with%20dark%20wood%20paneling%2C%20identity%20completely%20obscured%2C%20cinematic%20noir%20atmosphere%2C%20powerful%20and%20methodical%20presence%2C%20high%20contrast%20editorial%20photography&width=480&height=600&seq=team-mystery-03b&orientation=portrait',
    tagline: 'The Operator',
    hint: 'Nothing is left to chance. Nothing is left undone.',
    traits: ['Asset Oversight', 'Operational Precision', 'Zero Tolerance for Mediocrity'],
    detail: 'Every property under management is a direct reflection of this principal\'s standards. Flawless execution is not a goal — it is the baseline.',
  },
];

const STAGGER_MS = 150;

export default function MeetTheTeam() {
  const [headerRef, headerInView] = useInView<HTMLDivElement>({ threshold: 0.2 });
  const [cardsRef, cardsInView] = useInView<HTMLDivElement>({ threshold: 0.08 });

  return (
    <section id="team" className="w-full bg-[#111111] py-28 px-6 md:px-16">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div
          ref={headerRef}
          className={`flex flex-col md:flex-row gap-12 md:gap-24 mb-20 transition-all duration-700 ease-out ${
            headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="md:w-1/3">
            <span className="text-xs uppercase tracking-[0.25em] text-[#C9A961] font-semibold">
              — The Principals
            </span>
          </div>
          <div className="md:w-2/3">
            <h2 className="text-3xl md:text-4xl font-semibold text-white leading-tight mb-6">
              Meet the Team
            </h2>
            <p className="text-white/50 text-base md:text-lg leading-relaxed font-light">
              Our principals operate with intention and discretion. Their identities, like their work, are shared only with those who have earned the privilege.
            </p>
          </div>
        </div>

        {/* Team Cards */}
        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          {team.map((member, index) => (
            <div
              key={member.initials}
              style={{ transitionDelay: cardsInView ? `${index * STAGGER_MS}ms` : '0ms' }}
              className={`group flex flex-col transition-all duration-700 ease-out ${
                cardsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
            >
              {/* Portrait */}
              <div className="relative w-full overflow-hidden cursor-pointer" style={{ aspectRatio: '4/5' }}>
                <img
                  src={member.image}
                  alt={`Principal ${member.initials}`}
                  className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-108"
                  style={{ transform: undefined }}
                />

                {/* Base gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/10 transition-opacity duration-500 group-hover:opacity-0" />

                {/* Hover overlay — full dark cover */}
                <div className="absolute inset-0 bg-black/75 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {/* Roman numeral badge */}
                <div className="absolute top-5 left-5 w-10 h-10 flex items-center justify-center border border-[#C9A961]/60 group-hover:border-[#C9A961] text-[#C9A961] text-xs font-light tracking-widest transition-colors duration-300 z-10">
                  {member.initials}
                </div>

                {/* Default tagline — fades out on hover */}
                <div className="absolute bottom-5 left-5 right-5 transition-all duration-400 ease-out group-hover:opacity-0 group-hover:translate-y-3">
                  <p className="text-white text-lg font-semibold tracking-wide mb-1">
                    {member.tagline}
                  </p>
                  <p className="text-white/50 text-xs font-light italic leading-relaxed">
                    {member.hint}
                  </p>
                </div>

                {/* Hover reveal panel — slides up */}
                <div className="absolute inset-0 flex flex-col justify-end p-6 translate-y-6 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 ease-out z-10">
                  {/* Tagline stays */}
                  <p className="text-[#C9A961] text-xs uppercase tracking-[0.25em] font-semibold mb-3">
                    {member.tagline}
                  </p>

                  {/* Detail text */}
                  <p className="text-white/80 text-sm font-light leading-relaxed mb-5">
                    {member.detail}
                  </p>

                  {/* Trait pills */}
                  <div className="flex flex-col gap-2">
                    {member.traits.map((trait) => (
                      <div key={trait} className="flex items-center gap-2">
                        <div className="w-1 h-1 rounded-full bg-[#C9A961] flex-shrink-0" />
                        <span className="text-xs text-white/60 tracking-wide font-light whitespace-nowrap">
                          {trait}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <a
                    href="/contact"
                    onClick={(e) => { e.preventDefault(); window.location.href = '/contact'; }}
                    className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#C9A961] font-semibold border-b border-[#C9A961]/40 pb-0.5 hover:border-[#C9A961] transition-colors w-fit whitespace-nowrap"
                  >
                    Request Introduction
                    <i className="ri-arrow-right-line text-sm"></i>
                  </a>
                </div>
              </div>

              {/* Role */}
              <div className="pt-5 pb-4 border-b border-white/10">
                <p className="text-xs uppercase tracking-[0.2em] text-[#C9A961] font-medium">
                  {member.role}
                </p>
              </div>

              {/* Redacted bio */}
              <div className="pt-5">
                <div className="flex flex-col gap-2.5">
                  {[70, 90, 55, 80, 65].map((w, i) => (
                    <div
                      key={i}
                      className="h-2 rounded-full bg-white/10"
                      style={{ width: `${w}%` }}
                    />
                  ))}
                </div>
                <p className="mt-5 text-xs text-white/25 italic font-light">
                  Details available upon introduction.
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Rule */}
        <div
          className={`mt-20 pt-12 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-6 transition-all duration-700 ease-out ${
            cardsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ transitionDelay: cardsInView ? `${team.length * STAGGER_MS + 100}ms` : '0ms' }}
        >
          <p className="text-sm text-white/35 font-light max-w-xl leading-relaxed italic">
            &ldquo;We do not seek recognition. We seek results.&rdquo;
          </p>
          <a
            href="/contact"
            onClick={(e) => {
              e.preventDefault();
              window.location.href = '/contact';
            }}
            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#C9A961] font-semibold border-b border-[#C9A961]/40 pb-0.5 hover:border-[#C9A961] transition-colors cursor-pointer whitespace-nowrap"
          >
            Request an Introduction
            <i className="ri-arrow-right-line text-sm"></i>
          </a>
        </div>

      </div>
    </section>
  );
}
